(() => {
  const API_KEY = '__biceekHeroMotion';

  if (window[API_KEY]) {
    window[API_KEY].refresh();
    return;
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileViewport = window.matchMedia('(max-width: 749px)');
  const controllers = new Map();

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const toPercent = (value) => `${value.toFixed(2)}%`;

  class HeroEntranceController {
    constructor(hero) {
      this.hero = hero;
      this.intro = this.findIntro();
      this.observer = null;
      this.enterFrame = 0;
      this.startFrame = 0;
      this.settleTimer = 0;
      this.started = false;
      this.mount();
    }

    findIntro() {
      const heroSection = this.hero.closest('.shopify-section') || this.hero.parentElement;
      let sibling = heroSection?.nextElementSibling;

      while (sibling) {
        const intro = sibling.matches?.('[data-bf-landing-intro]')
          ? sibling
          : sibling.querySelector?.('[data-bf-landing-intro]');

        if (intro) return intro;
        if (sibling.matches?.('.shopify-section')) return null;
        sibling = sibling.nextElementSibling;
      }

      return null;
    }

    get enabled() {
      return this.hero.dataset.bfEnableMotion === 'true' && !reducedMotion.matches;
    }

    get elements() {
      return [this.hero, this.intro].filter(Boolean);
    }

    mount() {
      if (!this.enabled) {
        this.setStatic();
        return;
      }

      this.setEntryVariables();
      this.setState('pending', true);

      if (!('IntersectionObserver' in window)) {
        this.start();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.start();
        },
        {threshold: 0.01},
      );
      this.observer.observe(this.hero);
    }

    setEntryVariables() {
      const mobile = mobileViewport.matches;
      const intensity = clamp(
        Number(
          mobile
            ? this.hero.dataset.bfMobileMotionIntensity
            : this.hero.dataset.bfMotionIntensity,
        ) / 100 || 1,
        0.4,
        1.5,
      );
      const path = mobile
        ? {leftX: -18, leftY: -7, rightX: 18, rightY: 9}
        : {leftX: -24, leftY: -9, rightX: 24, rightY: 11};

      this.hero.style.setProperty('--bf-motion-left-entry-x', toPercent(path.leftX * intensity));
      this.hero.style.setProperty('--bf-motion-left-entry-y', toPercent(path.leftY * intensity));
      this.hero.style.setProperty('--bf-motion-right-entry-x', toPercent(path.rightX * intensity));
      this.hero.style.setProperty('--bf-motion-right-entry-y', toPercent(path.rightY * intensity));

      if (this.intro) {
        this.intro.style.setProperty('--bf-motion-right-entry-x', toPercent(path.rightX * intensity));
        this.intro.style.setProperty('--bf-motion-right-entry-y', toPercent(path.rightY * intensity));
      }
    }

    setState(state, active) {
      this.elements.forEach((element) => {
        element.dataset.bfMotionState = state;
        element.dataset.bfMotionActive = String(active);
      });
    }

    start() {
      if (this.started || !this.enabled) return;
      this.started = true;
      this.observer?.disconnect();

      this.startFrame = window.requestAnimationFrame(() => {
        this.enterFrame = window.requestAnimationFrame(() => {
          this.setState('entering', true);
          this.settleTimer = window.setTimeout(() => this.settle(), 1450);
        });
      });
    }

    settle() {
      if (!this.started) return;
      this.setState('settled', false);
    }

    setStatic() {
      this.elements.forEach((element) => {
        delete element.dataset.bfMotionState;
        delete element.dataset.bfMotionActive;
        [
          '--bf-motion-left-entry-x',
          '--bf-motion-left-entry-y',
          '--bf-motion-right-entry-x',
          '--bf-motion-right-entry-y',
        ].forEach((property) => element.style.removeProperty(property));
      });
    }

    destroy() {
      this.observer?.disconnect();
      window.cancelAnimationFrame(this.startFrame);
      window.cancelAnimationFrame(this.enterFrame);
      window.clearTimeout(this.settleTimer);
      this.started = false;
      this.setStatic();
    }
  }

  function refresh() {
    controllers.forEach((controller) => controller.destroy());
    controllers.clear();

    document.querySelectorAll('[data-bf-landing-hero]').forEach((hero) => {
      controllers.set(hero, new HeroEntranceController(hero));
    });
  }

  function removeWithin(scope) {
    controllers.forEach((controller, hero) => {
      if (scope.contains(hero) || (controller.intro && scope.contains(controller.intro))) {
        controller.destroy();
        controllers.delete(hero);
      }
    });
  }

  reducedMotion.addEventListener('change', refresh);
  mobileViewport.addEventListener('change', refresh);
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:unload', (event) => removeWithin(event.target));

  window[API_KEY] = {refresh};

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh, {once: true});
  } else {
    refresh();
  }
})();
